import { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { redirectToLogin } from "../utils/auth";
import { TicketProgressBar } from "../Components/Ticket/TicketProgressBar";
import { Loading } from "../Components/Loading/Loading";
import { useUser } from "../context/UserContext";

// 44px-high single-line control so native inputs/selects line up.
const controlClass =
  "w-full h-11 border border-gray-300 px-3 rounded-lg text-xs bg-white focus:outline-none focus:border-jiojbmpurple focus:ring-1 focus:ring-jiojbmpurple disabled:bg-gray-50 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed";

// Format a stored deadline into a local "YYYY-MM-DDTHH:mm" for a datetime-local input.
const toDatetimeLocal = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

// RCS external-client ticket view. External-only: severity/deadline/resolution
// are read-only, the assignee always shows "Sandesh RCS NNI Support", and there
// is no internal comments/history/audit access.
export const Ticket = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [ticket, setTicket] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [requestType, setRequestType] = useState();
  const [priority, setPriority] = useState();
  const [issueAt, setIssueAt] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [priorities, setpriorities] = useState([]);
  const [requestOptions, setrequestOptions] = useState([]);
  const [collaborators, setCollaborators] = useState("");
  const [deadline, setdeadline] = useState("");
  const [status, setstatus] = useState();
  const [statusList, setStatusList] = useState([]);
  const [resolution, setResolution] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [info, setInfo] = useState([]);
  const [isNeedMoreInfo, setIsNeedMoreInfo] = useState("0");
  const authToken = localStorage.getItem("authToken");
  const navigate = useNavigate();
  const location = useLocation("");

  const { user } = useUser();

  const tabs = useMemo(
    () => [
      { heading: "Request Details", id: "requestDetails" },
      { heading: "Discussion", id: "discussion" },
      { heading: "Resolution", id: "resolution" },
      { heading: "Media Upload", id: "mediaUpload" },
    ],
    [],
  );

  useEffect(() => {
    if (!authToken) {
      redirectToLogin();
      return;
    }
    if (!user.loaded) return;

    const BASE = process.env.REACT_APP_BACKEND_URL;
    const ticketId = location.pathname.slice(8);
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    };
    const get = (url) =>
      fetch(url, { method: "GET", headers }).then((r) => {
        if (r.status === 401) {
          redirectToLogin();
          return null;
        }
        return r.json();
      });

    const calls = [
      get(`${BASE}/API/V2/Tickets/${ticketId}`),
      get(`${BASE}/API/V2/config/priorities`),
      get(`${BASE}/API/V2/config/requesttypes`),
      get(`${BASE}/API/V2/config/status`),
      get(`${BASE}/API/V2/Tickets/${ticketId}/media`),
      get(`${BASE}/API/V2/Tickets/${ticketId}/additional-info`),
    ];

    Promise.all(calls)
      .then(([ticketData, prioritiesData, requestTypesData, statusData, filesData, additionalInfoData]) => {
        if (!ticketData) return;
        if (ticketData.error) {
          if (ticketData.error === "Invalid Organisation ID") {
            alert("Invalid Organisation ID");
            redirectToLogin();
          }
          return;
        }
                const sortedPriorities = [...prioritiesData].sort(
          (a, b) => Number(a.value) - Number(b.value)
        );

        setTicket(ticketData);
        setTitle(ticketData.title);
        setDescription(ticketData.description);
        setRequestType(ticketData.requestType);
        setPriority(ticketData.priority);
        setIssueAt(ticketData.issueAt || "");
        setdeadline(ticketData.deadline);
        setstatus(ticketData.status);
        setResolution(ticketData.resolution || "");
        setCollaborators(ticketData.externalCollaborators || "");
        setIsNeedMoreInfo(ticketData.isNeedMoreInfo);

        setpriorities(sortedPriorities || []);
        setrequestOptions(requestTypesData || []);
        setStatusList(statusData || []);
        setUploadedFiles(filesData || []);
        setInfo(additionalInfoData || []);
      })
      .catch((err) => console.error("Ticket load error:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.loaded]);

  const fileSelectedHandler = (event) => {
    const incoming = Array.from(event.target.files);
    const combined = [...selectedFiles, ...incoming];
    if (combined.length > 10) {
      alert("You can upload a maximum of 10 files.");
    }
    setSelectedFiles(combined.slice(0, 10));
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const removeFileHandler = async (fileName) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/API/V2/Tickets/${ticket.ticketId}/media/${fileName}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${authToken}` } },
      );
      if (!response.ok) throw new Error("Failed to delete file");
      setUploadedFiles((prevFiles) => prevFiles.filter((file) => file !== fileName));
    } catch (error) {
      console.error("Error deleting file:", error);
      alert("An error occurred while deleting the file.");
    }
  };

  function update() {
    if (!authToken) {
      alert("Unauthorized access. Please log in.");
      return;
    }
    if (!description || !priority || !deadline || !status) {
      alert("Please fill all required fields.");
      return;
    }

    const formData = new FormData();
    formData.append("description", description);
    selectedFiles.forEach((file, index) => {
      formData.append(`files[${index}]`, file);
    });
    formData.append("additionalInfo", additionalInfo);
    if (status == 5) {
      formData.append("isNeedMoreInfo", "1");
    }
    formData.append("status", status);

    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/API/V2/Tickets/${location.pathname.slice(8)}`,
      {
        method: "PUT",
        headers: { Authorization: `Bearer ${authToken}` },
        body: formData,
      },
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          alert("Failed to update the ticket: " + data.error);
        } else {
          alert("Ticket updated successfully!");
          navigate("/dashboard");
        }
      })
      .catch((error) => {
        console.error("Error updating ticket:", error);
        alert("An error occurred while updating the ticket.");
      });
  }

  function getStatusKey(value) {
    const match = statusList.find((s) => s.value == value);
    return match?.key ?? `Unknown (${value})`;
  }

  const handleDownload = async (fileName) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/API/V2/Tickets/${ticket.ticketId}/media/${fileName}`,
        { method: "GET", headers: { Authorization: `Bearer ${authToken}` } },
      );
      if (!response.ok) throw new Error("Failed to download file");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  if (!user.loaded || !ticket) {
    return <Loading />;
  }

  const activeTabId = tabs[activeTab]?.id;

  // Raiser edit rules:
  //  - Description & media editable only when Open (1) or Need More Info (5).
  //  - Status transitions: 5→{1,3}, 3→{1,4}, 4→{1}; Open/Active are locked.
  const canEditContent = ["1", "5"].includes(String(status));
  const RCS_STATUS_FLOW = {
    5: ["5", "1", "2"],
    3: ["3", "1", "4"],
    4: ["4", "1"],
  };
  const allowedStatus = RCS_STATUS_FLOW[String(status)] || [String(status)];

  

  return (
    <div className="flex h-[calc(100vh-72px)] overflow-hidden ">
      {/* Left — Ticket content */}
      <div className="flex-1 flex flex-col overflow-y-auto">
        <div className="flex flex-col p-6 min-h-full">
          {/* Ticket ID + Title header */}
          <div className="flex items-center gap-4 text-sm flex-shrink-0">
            <div className="flex items-center p-3 py-1 w-fit bg-jiojbm50 rounded-lg text-white font-black tracking-tight leading-snug">
              <p>{ticket.ticketId}</p>
            </div>
            <div className="font-black tracking-tight leading-snug uppercase">
              {title}
            </div>
          </div>

          {/* Tab Bar */}
          <div className="flex items-center ml-1 gap-6 mt-4 flex-shrink-0">
            {tabs.map((tab, index) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(index)}
                className="text-xs font-bold leading-snug transition-colors relative"
              >
                {tab.heading}
                {activeTab === index && (
                  <div className="absolute -bottom-1 left-0 right-0 h-1 rounded-full bg-jiojbmpurple" />
                )}
              </button>
            ))}
            <div className="ml-auto flex items-center">
              <button
                className="text-xs rounded-3xl px-3 py-1 font-bold tracking-tight leading-tight text-white bg-jiojbmpurple hover:opacity-90"
                type="button"
                onClick={() => update()}
              >
                Update
              </button>
            </div>
          </div>

          {/* Tab: Request Details */}
          {activeTabId === "requestDetails" && (
            <div className="flex flex-col gap-2 mt-6 ml-1 pb-6 text-xs flex-1">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 grid grid-cols-2 gap-4 flex-1">
                <div className="flex flex-col gap-1">
                  <p>
                    Description <span className="text-jiored1">*</span>
                  </p>
                  <textarea
                    className="w-full flex-1 min-h-[10rem] border border-gray-300 px-3 py-2 resize-none rounded-lg text-xs focus:outline-none focus:border-jiojbmpurple focus:ring-1 focus:ring-jiojbmpurple disabled:bg-gray-50 disabled:border-gray-200 disabled:cursor-not-allowed"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={!canEditContent}
                  />
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-4 self-start">
                  <div className="flex flex-col gap-1">
                    <p>
                      Service Request Type <span className="text-jiored1">*</span>
                    </p>
                    <select className={controlClass} value={requestType} disabled>
                      <option value="">--Select Request--</option>
                      {requestOptions.map((o, index) => (
                        <option key={index} value={o.value}>
                          {o.key}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* <div className="flex flex-col gap-1">
                    <p>Issue At</p>
                    <input
                      type="text"
                      className={controlClass}
                      value={issueAt}
                      disabled
                      readOnly
                    />
                  </div> */}
                  <div className="flex flex-col gap-1">
                    <p>
                      Severity <span className="text-jiored1">*</span>
                    </p>
                    <select
                      className={controlClass}
                      value={priority}
                      onChange={(e) => setPriority(e.target.value)}
                      disabled
                    >
                      <option value="">--Select Severity--</option>
                      {priorities.map((p, index) => (
                        <option key={index} value={p.value}>
                          {p.key}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <p>
                      Deadline <span className="text-jiored1">*</span>
                    </p>
                    <input
                      type="datetime-local"
                      className={controlClass}
                      value={toDatetimeLocal(deadline)}
                      onChange={(e) => setdeadline(e.target.value)}
                      disabled
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <p>
                      Status <span className="text-jiored1">*</span>
                    </p>
                    <select
                      className={controlClass}
                      value={status}
                      onChange={(e) => {
                        setstatus(e.target.value);
                        if (e.target.value == 5) {
                          setIsNeedMoreInfo("1");
                        }
                      }}
                    >
                      {["1", "2", "3", "5", "4"].map((s) => (
                        <option
                          key={s}
                          value={s}
                          disabled={!allowedStatus.includes(s)}
                        >
                          {getStatusKey(s)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <p>
                      Assigned <span className="text-jiored1">*</span>
                    </p>
                    <input
                      type="text"
                      className={controlClass}
                      value={
                        status == 5 || status == 3 || status == 4
                          ? ticket.createdBy || ""
                          : "Sandesh RCS NNI Support"
                      }
                      disabled
                      readOnly
                    />
                  </div>
                  <div className="flex flex-col gap-1 col-span-2">
                    <p>Collaborators</p>
                    <input
                      type="text"
                      className={controlClass}
                      value={collaborators}
                      disabled
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab: Discussion */}
          {activeTabId === "discussion" && (
            <div className="flex flex-col gap-2 mt-6 ml-1 pb-6 text-xs flex-1">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex-1 flex flex-col">
                <div className="flex flex-col gap-1 flex-1">
                  <p>Additional Information</p>
                  <textarea
                    placeholder={status == 3 ? "Discussion is closed for resolved tickets" : "Add Additional Information"}
                    className="w-full flex-1 min-h-[5rem] border border-gray-300 bg-white px-3 py-2 rounded-lg text-xs focus:outline-none focus:border-jiojbmpurple focus:ring-1 focus:ring-jiojbmpurple disabled:bg-gray-50 disabled:cursor-not-allowed"
                    value={additionalInfo}
                    onChange={(e) => setAdditionalInfo(e.target.value)}
                    disabled={status == 3 || status == 4}
                  ></textarea>
                </div>
                {info.length > 0 && (
                  <div className="mt-4 space-y-3 max-h-64 overflow-y-auto">
                    {info.map((comment, index) => (
                      <div
                        key={index}
                        className="w-full border bg-gray-100 py-2 px-3 rounded-lg"
                      >
                        <div className="flex items-center space-x-2">
                          <p className="font-semibold text-gray-700">
                            {comment.commentedBy}
                          </p>
                          <span className="text-gray-500">
                            {comment.createdAt &&
                              new Date(comment.createdAt).toLocaleString()}
                          </span>
                        </div>
                        <p className="mt-1 text-gray-600 whitespace-pre-line break-words">
                          {comment.commentText}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tab: Resolution */}
          {activeTabId === "resolution" && (
            <div className="flex flex-col gap-2 mt-6 ml-1 pb-6 text-xs flex-1">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex-1 flex flex-col">
                <div className="flex flex-col gap-1 flex-1">
                  <p>Resolution Description</p>
                  <textarea
                    className="w-full flex-1 min-h-[10rem] border border-gray-300 px-3 py-2 resize-none rounded-lg text-xs focus:outline-none focus:border-jiojbmpurple focus:ring-1 focus:ring-jiojbmpurple disabled:bg-gray-50 disabled:border-gray-200 disabled:cursor-not-allowed"
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value)}
                    disabled
                  />
                </div>
              </div>
            </div>
          )}

          {/* Tab: Media Upload */}
          {activeTabId === "mediaUpload" && (
            <div className="flex flex-col gap-2 mt-6 ml-1 pb-6 text-xs flex-1">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex-1">
                <input
                  className="block w-full text-xs text-gray-600 file:mr-3 file:cursor-pointer file:rounded-full file:border-0 file:bg-jiojbmpurple file:px-4 file:py-2 file:font-bold file:text-white hover:file:opacity-90 disabled:opacity-50 disabled:file:cursor-not-allowed"
                  type="file"
                  onChange={fileSelectedHandler}
                  multiple
                  accept=".xls,.xlsx,.pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.json,.csv,.pcap,.pcapng,.mp4,.mov,.avi,.mkv,.webm,.m4v"
                  disabled={!canEditContent}
                />
                {selectedFiles.length > 0 && (
                  <div className="mt-4">
                    <p className="font-semibold">Selected Files:</p>
                    <ul>
                      {selectedFiles.map((file, index) => (
                        <li
                          key={index}
                          className="flex justify-between items-center border border-gray-300 p-2 px-3 my-1 rounded-lg"
                        >
                          <span
                            className="text-blue-500 hover:underline cursor-pointer"
                            onClick={() => handleDownload(file)}
                          >
                            {file.name}
                          </span>
                          <button
                            type="button"
                            className="text-jiored1 hover:text-jiored1"
                            onClick={() => removeSelectedFile(index)}
                          >
                            ❌
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {uploadedFiles.length > 0 && (
                  <div className="mt-4">
                    <p className="font-semibold">Uploaded Files:</p>
                    <ul>
                      {uploadedFiles.map((file, index) => (
                        <li
                          key={index}
                          className="flex justify-between items-center bg-gray-100 p-2 my-1 rounded-md"
                        >
                          <span
                            className="text-blue-500 hover:underline cursor-pointer"
                            onClick={() => handleDownload(file)}
                          >
                            {file}
                          </span>
                          <button
                            type="button"
                            className="text-jiored1 hover:text-jiored1"
                            onClick={() => removeFileHandler(file)}
                          >
                            ❌
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right — Vertical Progress Bar */}
      <div className="flex justify-center">
        <TicketProgressBar
          ticketId={ticket.ticketId}
          authToken={authToken}
          ticket={ticket}
          isPartner={true}
        />
      </div>
    </div>
  );
};
